"use client";

import { cn } from "@newxsystems/shared";
import { useCallback, useEffect, useRef, useState } from "react";
import type { FocusEvent } from "react";
import { BlockActions } from "../../../shared/components/BlockActions/BlockActions";
import { BlockCommonContent } from "../../../shared/components/BlockCommonContent/BlockCommonContent";
import { BlockContainer } from "../../../shared/components/BlockContainer/BlockContainer";
import { LogoCloudItem } from "../LogoCloudItem/LogoCloudItem";
import type { ILogoCloudCarouselProps } from "./LogoCloudCarousel.props";

const pixelsPerSecond = 28;
const focusRevealPadding = 8;
const getTransform = (position: number) => `translate3d(${position}px, 0px, 0px)`;

type FocusedItem = {
  index: number;
  offset: number;
};

export const LogoCloudCarousel = ({
  alignment,
  anchorId,
  buttons,
  description,
  eyebrow,
  heading,
  items,
  spacing,
}: ILogoCloudCarouselProps) => {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const firstItemRef = useRef<HTMLDivElement | null>(null);
  const repeatedFirstItemRef = useRef<HTMLDivElement | null>(null);
  const positionRef = useRef(0);
  const [cycleWidth, setCycleWidth] = useState(0);
  const [initialPosition, setInitialPosition] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [focusedItem, setFocusedItem] = useState<FocusedItem | null>(null);

  const measureCycle = useCallback(() => {
    const firstItem = firstItemRef.current;
    const repeatedFirstItem = repeatedFirstItemRef.current;

    if (!firstItem || !repeatedFirstItem) {
      return;
    }

    const nextCycleWidth = repeatedFirstItem.offsetLeft - firstItem.offsetLeft;

    setCycleWidth((currentCycleWidth) =>
      currentCycleWidth === nextCycleWidth ? currentCycleWidth : nextCycleWidth,
    );
  }, []);

  useEffect(() => {
    measureCycle();

    const track = trackRef.current;
    const resizeObserver =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measureCycle);

    if (track) {
      resizeObserver?.observe(track);
    }

    window.addEventListener("resize", measureCycle);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", measureCycle);
    };
  }, [measureCycle]);

  useEffect(() => {
    if (cycleWidth <= 0) {
      return;
    }

    const nextPosition = -cycleWidth;
    positionRef.current = nextPosition;
    setInitialPosition(nextPosition);
    trackRef.current?.style.setProperty("transform", getTransform(nextPosition));
  }, [cycleWidth]);

  useEffect(() => {
    if (cycleWidth <= 0 || isHovered || isFocused) {
      return;
    }

    let animationFrame = 0;
    let lastTimestamp = performance.now();

    const animate = (timestamp: number) => {
      const elapsed = Math.min(timestamp - lastTimestamp, 100);
      lastTimestamp = timestamp;

      const nextPosition = positionRef.current + (elapsed / 1000) * pixelsPerSecond;
      const wrappedPosition =
        nextPosition >= 0 ? -cycleWidth + (nextPosition % cycleWidth) : nextPosition;

      positionRef.current = wrappedPosition;
      trackRef.current?.style.setProperty("transform", getTransform(wrappedPosition));
      animationFrame = requestAnimationFrame(animate);
    };

    animationFrame = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationFrame);
    };
  }, [cycleWidth, isFocused, isHovered]);

  const revealFocusedItem = (event: FocusEvent<HTMLDivElement>) => {
    const viewport = viewportRef.current;

    if (!viewport || !(event.target instanceof HTMLElement)) {
      setFocusedItem(null);
      return;
    }

    const focusedElement = event.target.closest<HTMLElement>(
      '[data-slot="carousel-item"]:not([aria-hidden="true"])',
    );
    const originalItems = Array.from(
      viewport.querySelectorAll<HTMLElement>(
        '[data-slot="carousel-item"]:not([aria-hidden="true"])',
      ),
    );
    const itemIndex = focusedElement ? originalItems.indexOf(focusedElement) : -1;

    if (itemIndex < 0 || !focusedElement) {
      setFocusedItem(null);
      return;
    }

    const viewportRect = viewport.getBoundingClientRect();
    const itemRect = focusedElement.getBoundingClientRect();
    const leftEdge = viewportRect.left + focusRevealPadding;
    const rightEdge = viewportRect.right - focusRevealPadding;
    const offset =
      itemRect.left < leftEdge
        ? leftEdge - itemRect.left
        : itemRect.right > rightEdge
          ? rightEdge - itemRect.right
          : 0;

    setFocusedItem({ index: itemIndex, offset });
  };

  const handleFocus = (event: FocusEvent<HTMLDivElement>) => {
    revealFocusedItem(event);
    setIsFocused(true);
  };

  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    const nextTarget = event.relatedTarget;

    if (!(nextTarget instanceof Node) || !event.currentTarget.contains(nextTarget)) {
      setFocusedItem(null);
      setIsFocused(false);
    }
  };

  const actions = buttons ?? [];
  const hasCommonContent = Boolean(eyebrow || heading || description);
  const hasActions = actions.length > 0;
  const hasIntroContent = hasCommonContent || hasActions;

  const renderItems = (presentationOnly: boolean) =>
    items.map((item, index) => {
      const isFocusedItem = !presentationOnly && focusedItem?.index === index;
      const focusTransform =
        isFocusedItem && focusedItem && focusedItem.offset !== 0
          ? { transform: getTransform(focusedItem.offset) }
          : undefined;

      return (
        <div
          aria-hidden={presentationOnly ? true : undefined}
          aria-roledescription="slide"
          className={cn("w-40 shrink-0", isFocusedItem && "z-10")}
          data-slot="carousel-item"
          inert={presentationOnly || undefined}
          key={`${presentationOnly ? "duplicate" : "original"}-${item.logo.defaultSrc}-${index}`}
          ref={index === 0 ? (presentationOnly ? repeatedFirstItemRef : firstItemRef) : undefined}
          role="group"
          style={focusTransform}
          tabIndex={presentationOnly ? -1 : undefined}
        >
          <LogoCloudItem
            {...item}
            className={cn("w-full", presentationOnly && "pointer-events-none")}
            presentationOnly={presentationOnly}
          />
        </div>
      );
    });

  return (
    <BlockContainer anchorId={anchorId} spacing={spacing}>
      {hasCommonContent ? (
        <BlockCommonContent
          alignment={alignment}
          description={description}
          eyebrow={eyebrow}
          heading={heading}
          headingLevel="h2"
        />
      ) : null}
      {hasActions ? (
        <BlockActions
          actions={actions}
          alignment={alignment}
          className={hasCommonContent ? "mt-8" : undefined}
        />
      ) : null}
      <div
        aria-label="Affiliated organizations"
        aria-roledescription="carousel"
        className={cn(
          "relative w-full min-w-0 overflow-hidden",
          hasIntroContent ? "mt-12 md:mt-16" : undefined,
        )}
        data-slot="carousel"
        ref={viewportRef}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        role="region"
      >
        <div className="w-full min-w-0 overflow-hidden" data-slot="carousel-content">
          <div
            className="flex w-max gap-4"
            ref={trackRef}
            style={{ transform: getTransform(initialPosition) }}
          >
            {renderItems(false)}
            {renderItems(true)}
          </div>
        </div>
      </div>
    </BlockContainer>
  );
};

LogoCloudCarousel.displayName = "LogoCloud::LogoCloudCarousel";
